import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";
import { formatPhoneNumber } from "capitalrx-utilities";

import { getPharmacyAPI } from "@/api/axios";
import { PharmacyInfo } from "@/api/types";
import { AppleMap } from "@/basic/components";
import { Card } from "@/premium/themedComponents";

import "./PharmacyDetailsPage.scss";

const TRANSLATION_PATH = "PHARMACY_DETAILS_PAGE";

export const PharmacyDetailsPanel = () => {
  const showAlert = useAlert();
  const [pharmacyData, setPharmacyData] = useState<PharmacyInfo>();
  const [isLoading, setIsLoading] = useState(false);
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const providerId = searchParams.get("providerId");

  const { halfColumns, hoursColumns } = useResponsive({
    halfColumns: { desktop: 6, tablet: 6, mobile: 12 },
    hoursColumns: { desktop: 2, tablet: 4, mobile: 3 },
  });

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const pharmacy = await getPharmacyAPI({ provider_id: providerId });
        setPharmacyData(pharmacy);
      } catch (e) {
        showAlert({
          message: translate("FIND_PHARMACIES.findPharmacies.mapsLoadingError"),
          type: "error",
          style: "banner",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [providerId]);

  const renderAddress = () => {
    const hasAddressLine2 = !!pharmacyData?.address_line_2;
    const hasCityStateZip = pharmacyData?.city && pharmacyData?.state && pharmacyData?.zip;

    return (
      <Body>
        {pharmacyData?.address_line_1}
        {hasAddressLine2 && (
          <span>
            <br />
            {pharmacyData?.address_line_2}
          </span>
        )}
        {hasCityStateZip && (
          <span>
            <br />
            {pharmacyData?.city}, {pharmacyData?.state}, {pharmacyData?.zip}
          </span>
        )}
      </Body>
    );
  };

  const formatHoursRow = (input: string) => {
    const parts = input.split(" - ");

    const day = parts[0];
    const hours = parts.slice(1).join(" - ") || "";

    return { day: day, hours: hours };
  };

  return (
    <Stack>
      <Heading>{translate(`${TRANSLATION_PATH}.pharmacyDetails`)}</Heading>
      <Card className="card">
        <LoadingGroup loading={isLoading}>
          <Stack>
            <Grid>
              <Grid.Span columns={12}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.billingProvider`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 100 }} />}>
                    <Body>{pharmacyData?.name || "--"}</Body>
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={halfColumns}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.address`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 200, height: 100 }} />}>
                    <Body>{renderAddress()}</Body>
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={halfColumns}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.phone`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 50 }} />}>
                    <Body>{formatPhoneNumber(pharmacyData?.phone) || "--"}</Body>
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={12}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.hours`)}</Body>
                  <LoadingGroup.Placeholder
                    fallback={
                      <Stack>
                        <LoadingSkeleton style={{ width: 80 }} />
                        <LoadingSkeleton style={{ width: 80 }} />
                        <LoadingSkeleton style={{ width: 80 }} />
                        <LoadingSkeleton style={{ width: 80 }} />
                        <LoadingSkeleton style={{ width: 80 }} />
                      </Stack>
                    }
                  >
                    {(pharmacyData?.hours || []).map((hour) => {
                      const { day, hours } = formatHoursRow(hour);
                      return (
                        <Grid key={day}>
                          <Grid.Span columns={hoursColumns}>
                            <Body>{day}</Body>
                          </Grid.Span>
                          <Grid.Span columns={12 - hoursColumns}>
                            <Body>{hours}</Body>
                          </Grid.Span>
                        </Grid>
                      );
                    })}
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={halfColumns}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.driveThru`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 50 }} />}>
                    <Body>{pharmacyData?.services.drive_up_window_indicator || "No"}</Body>
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={halfColumns}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.languages`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 50 }} />}>
                    {pharmacyData?.languages.length > 0 && <Body>{pharmacyData?.languages.join(", ")}</Body>}
                    {pharmacyData?.languages.length === 0 && <Body>N/A</Body>}
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              <Grid.Span columns={halfColumns}>
                <Stack spacing="mini">
                  <Body className="detail-label">{translate(`${TRANSLATION_PATH}.immunizations`)}</Body>
                  <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 50 }} />}>
                    <Body>{pharmacyData?.services.immunizations_provided_indicator || "No"}</Body>
                  </LoadingGroup.Placeholder>
                </Stack>
              </Grid.Span>
              {/* There is nothing in the data that we get back that pertains specifically to flu shots */}
              {/* <Grid.Span columns={halfColumns}> */}
              {/*  <Stack spacing="mini">*/}
              {/*    <Body className="detail-label">{translate(`${TRANSLATION_PATH}.fluShots`)}</Body>*/}
              {/*    <Body>N/A</Body>*/}
              {/*  </Stack>*/}
              {/* </Grid.Span> */}
              {/* There is nothing in the data that we get back that pertains to medicaid */}
              {/* <Grid.Span columns={halfColumns}> */}
              {/*  <Stack spacing="mini">*/}
              {/*    <Body className="detail-label">{translate(`${TRANSLATION_PATH}.medicaidEnrolled`)}</Body>*/}
              {/*    <Body>N/A</Body>*/}
              {/*  </Stack>*/}
              {/* </Grid.Span> */}
            </Grid>
            <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 500, height: 300 }} />}>
              {pharmacyData && (
                <AppleMap
                  style={{
                    height: "400px",
                  }}
                  locations={[
                    {
                      latitude: parseFloat(pharmacyData?.latitude),
                      longitude: parseFloat(pharmacyData?.longitude),
                      title: pharmacyData.name,
                    },
                  ]}
                />
              )}
            </LoadingGroup.Placeholder>
          </Stack>
        </LoadingGroup>
      </Card>
    </Stack>
  );
};
